import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import PreviewIcon from '@mui/icons-material/Preview'
import RecyclingIcon from '@mui/icons-material/Recycling'
import RestoreIcon from '@mui/icons-material/Restore'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import { Button } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { TableToolbarProps } from '../types'
import { toolbarButtonSx, toolbarIconSx } from '../styles'

export const TableToolbar = (props: TableToolbarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    selectionModel,
    checkbox,
    rows,
    titleKey,
    model,
    permissionView,
    permissionUpdate,
    permissionDelete,
    canShowEditButton,
    canShowRecycleButton,
    canShowUnarchiveButton,
    permissionUpdateRecycle,
    permissionUpdateUnarchive,
    goTo,
    onView,
    onDelete,
    onBulkDelete,
    onRecycle,
    onUnarchive,
    onActivity,
    onBeforeEdit,
    renderExtraToolbarActions,
    refreshData,
  } = props

  const isSingleSelection = selectionModel.length <= 1
  const isVisible = selectionModel.length > 0 && checkbox

  const getSelectedRow = () => {
    if (!isSingleSelection || selectionModel.length === 0) return null
    return rows.find((row) => row?.id === selectionModel[0])
  }

  const handleEditClick = () => {
    if (onBeforeEdit) {
      const row = getSelectedRow()
      if (row && !onBeforeEdit(row)) return
    }
    goTo?.(navigate, selectionModel[0])
  }

  const handleRecycleClick = () => {
    const row = getSelectedRow()
    if (row) onRecycle(row.id, row[titleKey])
  }

  const handleUnarchiveClick = () => {
    const row = getSelectedRow()
    if (row) onUnarchive(row.id, row[titleKey])
  }

  return (
    <Box
      component={'div'}
      sx={{
        height: '54px',
        display: isVisible ? 'flex' : 'none',
        position: 'absolute',
        zIndex: 10,
        backgroundColor: 'white',
        ml: '55px',
        width: '94%',
        gap: 2,
        alignItems: 'center',
      }}
    >
      {/* View */}
      {isSingleSelection && (
        <Button
          sx={toolbarButtonSx}
          onClick={() => onView(selectionModel[0])}
          disabled={permissionView}
        >
          <PreviewIcon sx={toolbarIconSx} />
          {t('table.button.cta.view')}
        </Button>
      )}

      {/* Edit */}
      {isSingleSelection && (
        <Button
          onClick={handleEditClick}
          sx={{
            display: canShowEditButton ? '' : 'none',
            ...toolbarButtonSx,
          }}
          disabled={permissionUpdate}
        >
          <EditIcon sx={toolbarIconSx} />
          {t('table.button.cta.edit')}
        </Button>
      )}

      {/* Recycle */}
      {isSingleSelection && (
        <Button
          onClick={handleRecycleClick}
          sx={{
            display: canShowRecycleButton ? '' : 'none',
            ...toolbarButtonSx,
          }}
          disabled={!permissionUpdateRecycle}
        >
          <RecyclingIcon sx={toolbarIconSx} />
          {t('table.button.cta.recycle')}
        </Button>
      )}

      {/* Unarchive */}
      {isSingleSelection && (
        <Button
          onClick={handleUnarchiveClick}
          sx={{
            display: canShowUnarchiveButton ? '' : 'none',
            ...toolbarButtonSx,
          }}
          disabled={!permissionUpdateUnarchive}
        >
          <UnarchiveIcon sx={toolbarIconSx} />
          {t('table.button.cta.unarchive')}
        </Button>
      )}

      {/* Activity */}
      {isSingleSelection && (
        <Button
          onClick={() => onActivity(selectionModel[0])}
          sx={toolbarButtonSx}
        >
          <RestoreIcon sx={toolbarIconSx} />
          {t('table.button.cta.activity')}
        </Button>
      )}

      {/* Extra toolbar actions (campaign-specific, etc.) */}
      {renderExtraToolbarActions?.({
        selectionModel,
        rows,
        refreshData,
      })}

      {/* Delete / Bulk Delete */}
      <Button
        onClick={onBulkDelete}
        sx={toolbarButtonSx}
        disabled={permissionDelete}
      >
        <DeleteForeverIcon sx={toolbarIconSx} />
        {isSingleSelection && t('table.button.cta.delete')}
        {selectionModel.length > 1 &&
          `${t('table.button.cta.delete')} ${t(
            `table.button.cta.model.${model}`
          )}`}
      </Button>
    </Box>
  )
}
